import React from "react"

/**
 * Tiny visual indicator showing which scope supplied a form field's current
 * default value. Rendered next to a field label by the form-field renderers
 * in `LegacyGraphRunwayPage.jsx` via the `sourceForKey(input_key)` helper.
 *
 * Density rule (per UX spec): callers should usually only render this badge
 * when the value differs from the next-lower scope (so most fields stay
 * uncluttered). The "show all sources" toggle in the form header lifts that
 * rule for debugging.
 *
 * Variants:
 *   - default (with label): "From account default"
 *   - `compact`: dot only with `aria-label` for screen readers
 *
 * Accessibility: color is never the only signal — the SR-only label is
 * always present, and the visible label is shown unless `compact`.
 *
 * Sources: "manifest" | "global" | "solution" | "account" | undefined
 */

const SOURCE_META = {
  manifest: {
    label: "From manifest default",
    short: "manifest",
    dotClass: "bg-slate-400",
    chipClass: "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-700/40 dark:text-slate-200 dark:ring-slate-600",
  },
  global: {
    label: "From global default",
    short: "global",
    dotClass: "bg-blue-500",
    chipClass: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:ring-blue-700",
  },
  solution: {
    label: "From solution default",
    short: "solution",
    dotClass: "bg-violet-500",
    chipClass: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-900/30 dark:text-violet-200 dark:ring-violet-700",
  },
  account: {
    label: "From account default",
    short: "account",
    dotClass: "bg-emerald-500",
    chipClass: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-700",
  },
}

export function DefaultSourceBadge({ source, compact = false, className = "" }) {
  const meta = SOURCE_META[source]
  if (!meta) return null

  if (compact) {
    return (
      <span
        className={`inline-block h-2 w-2 rounded-full ${meta.dotClass} ${className}`}
        role="img"
        aria-label={meta.label}
        title={meta.label}
        data-source={source}
        data-testid="default-source-badge"
      />
    )
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none ring-1 ring-inset ${meta.chipClass} ${className}`}
      title={meta.label}
      data-source={source}
      data-testid="default-source-badge"
    >
      <span aria-hidden="true" className={`inline-block h-1.5 w-1.5 rounded-full ${meta.dotClass}`} />
      <span className="sr-only">{meta.label}</span>
      <span aria-hidden="true">{meta.short}</span>
    </span>
  )
}

export default DefaultSourceBadge
